import { ReactNode, useState } from 'react'
import { Button, Group, Stack, Text } from '@common/ui'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { com } from '@eidu/web'
import PageLayout from '../shared/PageLayout'
import { KindViewPageProps } from '../KindPages'
import useHasPermission from '../../../permission/useHasPermission'
import { getResourceKeyFromEntity } from '../../../../io/entityResourceKeyLink'
import useResource from '../../../../io/useResource'
import useRuntimeEntity from '../../../../io/useRuntimeEntity'
import { AssetSourceView } from './AssetSourceView'
import { AssetPreviewView } from './AssetPreviewView'
import ScrollFadeContainer from '../../../../components/layout/ScrollFadeContainer'
import PersistentTabs, { PersistentTab } from '../../../../components/layout/PersistentTabs'
import { getKnownKindKey } from '../../../../domain/kindRegistry'
import EntityAction = com.eidu.entity.permission.EntityAction
import EntityFieldDisplayGroup from '../shared/EntityFieldDisplayGroup'
import useIsPublishable from './publish/useIsPublishable'
import PublishDialog from './publish/PublishDialog'
import VersionHistoryDrawer from './publish/VersionHistoryDrawer'
import usePublishStatus from './publish/usePublishStatus'
import PublishStatusChip from './publish/PublishStatusChip'

type AssetViewTab = 'preview' | 'source' | 'fields'

export type RenderSourceArgs = { content: string | undefined; isLoading: boolean }

const AssetViewPage = ({
  entity,
  entityType,
  entityTypes,
  fields,
  renderSource,
  renderPreview,
}: KindViewPageProps & {
  renderSource?: (args: RenderSourceArgs) => ReactNode
  renderPreview?: () => ReactNode
}) => {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const pinnedVersionId = searchParams.get('versionId') ?? undefined
  const initialDrawerOpen = searchParams.get('versionHistory') === '1'
  const initialCompareVersionId = searchParams.get('compareVersion') ?? undefined

  const { hasPermission: hasUpdatePermission } = useHasPermission(entity.entity.id, EntityAction.UpdateEntity)
  const { publishable } = useIsPublishable(entity, entityType)

  const navigateToEdit = () => {
    navigate(`/entities/${entityType.id.toString()}/edit/${entity.entity.id.toString()}`)
  }

  const currentResourceKey = getResourceKeyFromEntity(entity)
  const { content, isLoading } = useResource(currentResourceKey)
  const { runtimeEntityId } = useRuntimeEntity(entity)
  const hasRuntime = !!runtimeEntityId
  const showPreview = hasRuntime || !!renderPreview

  const [validationErrors, setValidationErrors] = useState<string[]>([])
  const publishStatus = usePublishStatus({ entity })

  return (
    <PageLayout
      title={entity.primaryLabelText}
      subtitle={entityType.name}
      auditTargetId={entity.entity.id.toString()}
      actions={
        <Group gap="xs">
          {publishable && (
            <>
              <PublishStatusChip
                status={publishStatus}
                entityType={entityType}
                currentResourceKey={currentResourceKey}
              />
              <PublishDialog entityId={entity.entity.id} />
              <VersionHistoryDrawer
                entityId={entity.entity.id}
                initialOpen={initialDrawerOpen}
                initialCompareVersionId={initialCompareVersionId}
              />
            </>
          )}
          {hasUpdatePermission && (
            <Button variant="subtle" onClick={navigateToEdit} data-testid="entity-action-edit">
              Edit
            </Button>
          )}
        </Group>
      }
      showBack
    >
      <Stack gap={0} flex={1} style={{ minHeight: 0 }}>
        <PersistentTabs<AssetViewTab>
          defaultValue={showPreview ? 'preview' : 'source'}
          style={{ marginBottom: 8, flexShrink: 0 }}
        >
          {showPreview && (
            <PersistentTab<AssetViewTab> label="Preview" value="preview">
              {renderPreview ? (
                renderPreview()
              ) : (
                <>
                  <AssetPreviewView
                    entity={entity}
                    entityKind={getKnownKindKey(entityType.kind)}
                    entityTypes={entityTypes}
                    pinnedVersionId={pinnedVersionId}
                    onValidationChange={(result) => setValidationErrors(result.valid ? [] : result.errors)}
                  />
                  {validationErrors.map((error) => (
                    <Text key={error} size="xs" c="negative">
                      {error}
                    </Text>
                  ))}
                </>
              )}
            </PersistentTab>
          )}
          <PersistentTab<AssetViewTab> label="Source" value="source">
            {renderSource ? (
              renderSource({ content, isLoading })
            ) : (
              <AssetSourceView content={content} isLoading={isLoading} />
            )}
          </PersistentTab>
          <PersistentTab<AssetViewTab> label="Fields" value="fields">
            <ScrollFadeContainer>
              <EntityFieldDisplayGroup entityType={entityType} fields={fields} />
            </ScrollFadeContainer>
          </PersistentTab>
        </PersistentTabs>
      </Stack>
    </PageLayout>
  )
}

export default AssetViewPage
